import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { AdminUser, LoginCredentials, ApiResponse } from '../types'
import { authService } from '../services/authService'
import { getStoredToken, setStoredToken, getStoredUser, setStoredUser, removeStoredToken, removeStoredUser } from '../utils'

interface AuthContextType {
  user: AdminUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => void
  error: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = getStoredToken()
        const storedUser = getStoredUser()

        if (token && storedUser) {
          // Validate token with API
          const response = await authService.validateToken()
          if (response.success) {
            setUser(storedUser)
          } else {
            // Token is invalid, clear stored data
            removeStoredToken()
            removeStoredUser()
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err)
        removeStoredToken()
        removeStoredUser()
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const login = async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true)
      setError(null)

      const response: ApiResponse<{ token: string; user: AdminUser }> = await authService.login(credentials)

      if (response.success && response.data) {
        const { token, user } = response.data

        // Store in localStorage
        setStoredToken(token)
        setStoredUser(user)

        // Update state
        setUser(user)
      } else {
        throw new Error(response.message || 'Login failed')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    // Clear stored data
    removeStoredToken()
    removeStoredUser()

    // Clear state
    setUser(null)
    setError(null)
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    error,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
